import React, { useState, forwardRef, useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Offcanvas, Form } from 'react-bootstrap';
import swal from "sweetalert";
import AdminLandsaleService from "../../../API/Services/AdminService/AdminLandsaleService";
import Select from "react-select";
import ClipLoader from 'react-spinners/ClipLoader';
import AdminBuilderService from '../../../API/Services/AdminService/AdminBuilderService';
import LazySubdivisionMultiSelect from "../../components/LazySubdivisionMultiSelect";

const LandsaleOffcanvas = forwardRef((props, ref) => {
    const { canvasShowAdd, seCanvasShowAdd } = props;

    const [error, setError] = useState({});
    const [apiError, setApiError] = useState('');
    const [builderCode, setBuilderCode] = useState([]);
    const [subdivisionCode, setSubdivisionCode] = useState([]);
    const [masterPlanCode, setMasterPlanCode] = useState([]);
    const [builderListDropDown, setBuilderListDropDown] = useState([]);
    const [masterPlanDropDownList, setMasterPlanDropDownList] = useState([]);
    const [noOfUnit, setNoOfUnit] = useState(null);
    const [price, setPrice] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (canvasShowAdd) {
            GetBuilderDropDownList();
            GetMasterPlanDropDownList();
        }
    }, [canvasShowAdd]);

    const GetBuilderDropDownList = async () => {
        setIsLoading(true);
        try {
            const response = await AdminBuilderService.builderDropDown();
            const responseData = await response.json();
            const formattedData = [
                { label: "Select Builder None", value: "" }, // Default option
                ...responseData.map((builder) => ({
                    label: builder.name,
                    value: builder.id,
                })),
            ];
            setBuilderListDropDown(formattedData);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            if (error.name === "HTTPError") {
                const errorJson = await error.response.json();
                setApiError("Something went wrong!");
            }
        }
    };

    const GetMasterPlanDropDownList = async () => {
        setIsLoading(true);
        try {
            const response = await AdminBuilderService.masterPlanDropDown();
            const responseData = await response.json();
            setMasterPlanDropDownList([
                { label: "Select Master Plan None", value: "" },
                ...responseData,
            ]);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            setApiError("Something went wrong!");
        }
    };

    const handleSelectBuilderNameChange = (code) => {
        setBuilderCode(code);
        setSubdivisionCode([]);
        setError((prev) => ({
            ...prev,
            builder_id: "",
            subdivision_id: "",
        }));
    };

    const handleSubdivisionCode = (code) => {
        setSubdivisionCode(code);
        setError((prev) => ({
            ...prev,
            subdivision_id: "",
        }));
        if (code && code.value && code.value !== "") {
            if (code.masterplan_id) {
                const matchedMasterPlan = masterPlanDropDownList.find(mp => mp.value === code.masterplan_id);
                if (matchedMasterPlan) {
                    setMasterPlanCode(matchedMasterPlan);
                    setError((prev) => ({
                        ...prev,
                        masterplan_id: "",
                    }));
                } else {
                    setMasterPlanCode({ label: "Select Master Plan None", value: "" });
                }
            } else {
                setMasterPlanCode({ label: "Select Master Plan None", value: "" });
            }
        }
    };

    const handleMasterPlanCode = (code) => {
        setMasterPlanCode(code);
        setError((prev) => ({
            ...prev,
            masterplan_id: "",
        }));
    };

    const handleFieldChange = (event) => {
        const { name } = event.target;
        setError((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    const getValidationError = (errors, field) => {
        const fieldError = errors?.[field];
        return Array.isArray(fieldError) ? fieldError[0] : fieldError || "";
    };

    const resetFormState = () => {
        setBuilderCode([]);
        setPrice(null);
        setNoOfUnit(null);
        setSubdivisionCode([]);
        setMasterPlanCode([]);
        setError({});
        setApiError('');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError({});
        setApiError('');
        try {
            var userData = {
                "seller": event.target.seller.value,
                "buyer": event.target.buyer.value,
                "location": event.target.location.value,
                "date": event.target.date.value,
                "parcel": event.target.parcel.value,
                "price": event.target.price.value ? event.target.price.value : 0,
                "typeofunit": event.target.typeofunit.value,
                "noofunit": event.target.noofunit.value,
                "notes": event.target.notes.value,
                "doc": event.target.doc.value,
                "zoning": event.target.zoning.value,
                "lat": event.target.lat.value,
                "lng": event.target.lng.value,
                "area": event.target.area.value,
                "zip": event.target.zip.value,
                "subdivision_id": (subdivisionCode?.value === "" || subdivisionCode?.value === undefined || subdivisionCode?.value === null) ? "" : subdivisionCode?.value,
                "masterplan_id": (masterPlanCode?.value === "" || masterPlanCode?.value === undefined || masterPlanCode?.value === null) ? "" : masterPlanCode?.value,
                "builder_id": (builderCode?.value === "" || builderCode?.value === undefined || builderCode?.value === null) ? "" : builderCode?.value,
            }

            const data = await AdminLandsaleService.store(userData).json();

            if (data.status === true) {
                swal("Landsale Created Succesfully").then((willDelete) => {
                    if (willDelete) {
                        props.parentCallback();
                        seCanvasShowAdd(false);
                        resetFormState();
                    }
                })
            }
        }
        catch (error) {
            if (error.name === 'HTTPError' && error.response) {
                const errorJson = await error.response.json().catch(() => ({}));

                if (error.response.status === 422) {
                    const validationErrors = errorJson?.errors || {};
                    setError({
                        seller: getValidationError(validationErrors, "seller"),
                        buyer: getValidationError(validationErrors, "buyer"),
                        location: getValidationError(validationErrors, "location"),
                        date: getValidationError(validationErrors, "date"),
                        parcel: getValidationError(validationErrors, "parcel"),
                        price: getValidationError(validationErrors, "price"),
                        typeofunit: getValidationError(validationErrors, "typeofunit"),
                        noofunit: getValidationError(validationErrors, "noofunit"),
                        notes: getValidationError(validationErrors, "notes"),
                        doc: getValidationError(validationErrors, "doc"),
                        zoning: getValidationError(validationErrors, "zoning"),
                        lat: getValidationError(validationErrors, "lat"),
                        lng: getValidationError(validationErrors, "lng"),
                        area: getValidationError(validationErrors, "area"),
                        zip: getValidationError(validationErrors, "zip"),
                        subdivision_id: getValidationError(validationErrors, "subdivision_id"),
                        builder_id: getValidationError(validationErrors, "builder_id"),
                        masterplan_id: getValidationError(validationErrors, "masterplan_id"),
                    });
                    setApiError(errorJson?.message || errorJson?.errors?.message || "");
                    return;
                }

                setApiError(
                    errorJson?.message ||
                    errorJson?.error ||
                    errorJson?.errors?.message ||
                    `Request failed (HTTP ${error.response.status}).`
                );
                return;
            }

            setApiError(error?.message || "Something went wrong. Please try again.");
        }
    };

    return (
        <Fragment>
            <Offcanvas show={canvasShowAdd} onHide={seCanvasShowAdd} className="offcanvas-end customeoff" placement='end'>
                <div className="offcanvas-header">
                    <h5 className="modal-title" id="#gridSystemModal">{props.Title}</h5>
                    <button type="button" className="btn-close"
                        onClick={() => { seCanvasShowAdd(false); resetFormState(); }}
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>
                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center mb-5 mt-5">
                        <ClipLoader color="#4474fc" />
                    </div>
                ) : (
                    <div className="offcanvas-body">
                        <div className="container-fluid">
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-xl-6 mb-3">
                                        <label className="form-label">Builder</label>
                                        <Form.Group controlId="tournamentList">
                                            <Select
                                                name="builder_name"
                                                options={builderListDropDown}
                                                value={builderCode}
                                                onChange={handleSelectBuilderNameChange}
                                                placeholder={"Select Builder Name"}
                                                styles={{
                                                    container: (provided) => ({
                                                        ...provided,
                                                        color: 'black'
                                                    }),
                                                    menu: (provided) => ({
                                                        ...provided,
                                                        color: 'black'
                                                    }),
                                                }}
                                            />
                                        </Form.Group>
                                        {error.builder_id && <p className="text-danger fs-12 mt-1">{error.builder_id}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label className="form-label">Subdivision</label>
                                        <Form.Group controlId="tournamentList">
                                            <LazySubdivisionMultiSelect
                                                builderIds={(builderCode?.value === "" || builderCode?.value === undefined || builderCode?.value === null) ? [] : [builderCode.value]}
                                                name="subdivision_id"
                                                value={subdivisionCode}
                                                onChange={(selectedOption) => handleSubdivisionCode(selectedOption)}
                                                placeholder={"Select Subdivision..."}
                                                isMulti={false}
                                            />
                                        </Form.Group>
                                        {error.subdivision_id && <p className="text-danger fs-12 mt-1">{error.subdivision_id}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label className="form-label">Master Plan</label>
                                        <Form.Group controlId="masterPlanList">
                                            <Select
                                                name="masterplan_id"
                                                options={masterPlanDropDownList}
                                                value={masterPlanCode}
                                                onChange={handleMasterPlanCode}
                                                placeholder={"Select Master Plan..."}
                                                styles={{
                                                    container: (provided) => ({
                                                        ...provided,
                                                        color: 'black'
                                                    }),
                                                    menu: (provided) => ({
                                                        ...provided,
                                                        color: 'black'
                                                    }),
                                                }}
                                            />
                                        </Form.Group>
                                        {error.masterplan_id && <p className="text-danger fs-12 mt-1">{error.masterplan_id}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput2" className="form-label">Seller <span className="text-danger">*</span></label>
                                        <input type="text" name='seller' className="form-control" id="exampleFormControlInput2" placeholder="" onChange={handleFieldChange} />
                                        {error.seller && <p className="text-danger fs-12 mt-1">{error.seller}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput3" className="form-label">Buyer <span className="text-danger">*</span></label>
                                        <input type="text" name='buyer' className="form-control" id="exampleFormControlInput3" placeholder="" onChange={handleFieldChange} />
                                        {error.buyer && <p className="text-danger fs-12 mt-1">{error.buyer}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput4" className="form-label">Location <span className="text-danger">*</span></label>
                                        <input type="text" name='location' className="form-control" id="exampleFormControlInput4" placeholder="" onChange={handleFieldChange} />
                                        {error.location && <p className="text-danger fs-12 mt-1">{error.location}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput5" className="form-label">Date <span className="text-danger">*</span></label>
                                        <input type="date" name='date' className="form-control" id="exampleFormControlInput5" placeholder="" onChange={handleFieldChange} />
                                        {error.date && <p className="text-danger fs-12 mt-1">{error.date}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput6" className="form-label">Parcel</label>
                                        <input type="text" name='parcel' className="form-control" id="exampleFormControlInput6" placeholder="" onChange={handleFieldChange} />
                                        {error.parcel && <p className="text-danger fs-12 mt-1">{error.parcel}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput7" className="form-label">Price ($)</label>
                                        <input type="number" name='price' className="form-control" id="exampleFormControlInput7" placeholder="" onChange={(e) => { setPrice(e.target.value); handleFieldChange(e); }} onWheel={(e) => e.currentTarget.blur()} onKeyDown={(e) => (e.key === "ArrowUp" || e.key === "ArrowDown") && e.preventDefault()} />
                                        {error.price && <p className="text-danger fs-12 mt-1">{error.price}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput10" className="form-label">Type of Unit</label>
                                        <input type="text" name='typeofunit' className="form-control" id="exampleFormControlInput10" placeholder="" onChange={handleFieldChange} />
                                        {error.typeofunit && <p className="text-danger fs-12 mt-1">{error.typeofunit}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput11" className="form-label">Price Per Unit ($)</label>
                                        <input type="number" disabled style={{ backgroundColor: "#f0f0f0", cursor: "not-allowed" }} value={noOfUnit ? Math.floor(price / noOfUnit) : 0} name='priceperunit' className="form-control" id="exampleFormControlInput11" placeholder="" onWheel={(e) => e.currentTarget.blur()} onKeyDown={(e) => (e.key === "ArrowUp" || e.key === "ArrowDown") && e.preventDefault()} />
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput12" className="form-label">No. Of Unit</label>
                                        <input step="any" type="number" name='noofunit' className="form-control" id="exampleFormControlInput12" placeholder="" onChange={(e) => { setNoOfUnit(e.target.value); handleFieldChange(e); }} onWheel={(e) => e.currentTarget.blur()} onKeyDown={(e) => (e.key === "ArrowUp" || e.key === "ArrowDown") && e.preventDefault()} />
                                        {error.noofunit && <p className="text-danger fs-12 mt-1">{error.noofunit}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput16" className="form-label">Notes</label>
                                        <input type="text" name='notes' className="form-control" id="exampleFormControlInput16" placeholder="" onChange={handleFieldChange} />
                                        {error.notes && <p className="text-danger fs-12 mt-1">{error.notes}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput17" className="form-label">Doc</label>
                                        <input type="text" name='doc' className="form-control" id="exampleFormControlInput17" placeholder="" onChange={handleFieldChange} />
                                        {error.doc && <p className="text-danger fs-12 mt-1">{error.doc}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput17" className="form-label">Zoning</label>
                                        <input type="text" name='zoning' className="form-control" id="exampleFormControlInput17" placeholder="" onChange={handleFieldChange} />
                                        {error.zoning && <p className="text-danger fs-12 mt-1">{error.zoning}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput17" className="form-label">Latitude</label>
                                        <input type="text" name='lat' className="form-control" id="exampleFormControlInput17" placeholder="" onChange={handleFieldChange} />
                                        {error.lat && <p className="text-danger fs-12 mt-1">{error.lat}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput17" className="form-label">Longitude</label>
                                        <input type="text" name='lng' className="form-control" id="exampleFormControlInput17" placeholder="" onChange={handleFieldChange} />
                                        {error.lng && <p className="text-danger fs-12 mt-1">{error.lng}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput17" className="form-label">Area</label>
                                        <input type="text" name='area' className="form-control" id="exampleFormControlInput17" placeholder="" onChange={handleFieldChange} />
                                        {error.area && <p className="text-danger fs-12 mt-1">{error.area}</p>}
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                        <label htmlFor="exampleFormControlInput17" className="form-label">ZIP Code</label>
                                        <input type="text" name='zip' className="form-control" id="exampleFormControlInput17" placeholder="" onChange={handleFieldChange} />
                                        {error.zip && <p className="text-danger fs-12 mt-1">{error.zip}</p>}
                                    </div>

                                    {apiError && <p className='text-danger fs-12 mt-2'>{apiError}</p>}
                                </div>

                                <div>
                                    <button type="submit" className="btn btn-primary me-1">Submit</button>
                                    <Link to={"#"} onClick={() => { seCanvasShowAdd(false); resetFormState(); }} className="btn btn-danger light ms-1">Cancel</Link>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </Offcanvas>
        </Fragment>
    );
});

export default LandsaleOffcanvas;
